/**
 * A sheet that slides over the board (docs/ux.md U-TOUCH-01): from the bottom on phones, from
 * the right on tablets; on desktops it is a drawer at the right edge (`side`), the left edge
 * (`left`) or over the bottom of the board (`dock`). Swipe it down (or tap ×, or Esc) to close.
 */
import { useId, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as styles from './Game.css.ts';
import { Icon, type IconName } from './icons.tsx';

/** How far a sheet must be pulled down to close, CSS px. */
const SWIPE_CLOSE_PX = 60;

export function BottomSheet({
  title,
  onClose,
  children,
  testId,
  placement = 'side',
  icon,
  actions,
}: {
  title: ReactNode;
  /** The icon of the HUD button that opened it, before the title. */
  icon?: IconName;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
  /** Desktop only: a drawer at the right (`side`) or left (`left`) edge, or over the bottom (`dock`). */
  placement?: 'side' | 'left' | 'dock';
  /** Buttons in the head, next to ×. */
  actions?: ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  const drag = useRef<{ y: number; dy: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const move = (dy: number) => {
    if (ref.current) ref.current.style.transform = dy > 0 ? `translateY(${dy}px)` : '';
  };
  return (
    <div
      ref={ref}
      className={`${styles.sheet} ${
        placement === 'dock' ? styles.sheetDock : placement === 'left' ? styles.sheetLeft : styles.sheetSide
      }`}
      role="dialog"
      aria-modal="false"
      aria-labelledby={id}
      data-testid={testId}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div
        className={styles.sheetHead}
        onPointerDown={(e) => {
          // The head's buttons keep their clicks: only the bare head drags the sheet.
          if (e.target instanceof Element && e.target.closest('button, a, input, select')) return;
          drag.current = { y: e.clientY, dy: 0 };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!drag.current) return;
          drag.current.dy = e.clientY - drag.current.y;
          move(drag.current.dy);
        }}
        onPointerUp={() => {
          const dy = drag.current?.dy ?? 0;
          drag.current = null;
          move(0);
          if (dy > SWIPE_CLOSE_PX) onClose();
        }}
        onPointerCancel={() => {
          drag.current = null;
          move(0);
        }}
      >
        <span className={styles.sheetGrip} aria-hidden="true" />
        <h2 id={id} className={styles.sheetTitle}>
          {icon ? <Icon name={icon} size={18} /> : null}
          {title}
        </h2>
        <span className={styles.spacer} />
        {actions}
        <button
          type="button"
          className={styles.sheetClose}
          aria-label={t('game.sheet.close')}
          data-tip={t('game.sheet.close')}
          data-key="Esc"
          onClick={onClose}
        >
          <Icon name="close" size={18} />
        </button>
      </div>
      <div className={styles.sheetBody}>{children}</div>
    </div>
  );
}
